import React, { useEffect, useRef, useState } from "react";
import styles from "./EnergyFlowDiagram.module.css";
import { ReactComponent as GridIcon } from "../../assets/segment_energy_infrastructure.svg";
import GensetBar from "./GensetBar";
import PVBar from "./PVBar";
import { ReactComponent as BatteryIcon } from "../../assets/battery2_general.svg";
import { ReactComponent as SiteIcon } from "../../assets/site.svg";
import { ReactComponent as LoadIcon } from "../../assets/segment_building.svg";
import { IconDownload, IconRotate } from "@tabler/icons-react";
import { useMantineColorScheme } from "@mantine/core";
import { formatValueToPercentage } from "./RESULT_CONFIG";
import PropTypes from "prop-types";
import {
  getLeftFlowPath,
  getRightMainFlowPath,
  getRightSplitFlowPath,
  getRightWindFlowPath
} from "./energyFlowPaths";
import { useTranslation } from "react-i18next";
import { formatEnUsValue } from "../../utils/helpers";

const INIT_Y = 400;
const INIT_X = 130;

const INIT_RY = 400;
const INIT_RX = 80; // top bar x
const INIT_RX_BOTTOM = 10; // bottom bar x
const INIT_RY_TOP = 150; // top bar y for right flow
const INIT_TRANS = { x: 63.1, y: 4.25 };
const BAR_WIDTH = 400 * 0.6; // Reduce width to 60% of original (was 400)
const BAR_HEIGHT = 20;
const BAR_MIN_Y = 0;
const BAR_MAX_Y = 2800;
const BAR_MIN_X = 0;
const BAR_MAX_X = 1000;
const MIRROR_X_OFFSET = BAR_WIDTH + 120;

const SVG_WIDTH = 2 * (BAR_WIDTH + 100) + 400; // increase width to allow full drag for extra bars
const SVG_HEIGHT = 650; // was 2200, now just enough for all bars



const EXTRA_BAR_HEIGHT = BAR_HEIGHT; // half height

const EnergyFlow = ({ data, scenario }) => {
    const { t } = useTranslation();
  const toNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    const normalized = String(value).replace(/,/g, "").trim();
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  // Assign all frequently used values to variables
  const batteryLoss = data?.componentsAttributes?.battery?.batteryLoss ?? 0;
  const pvElectrical = data?.componentsAttributes?.pv?.electrical ?? 0;
  const utilityImport = data?.componentsAttributes?.utility?.import ?? 0;
  const utilityExport = data?.componentsAttributes?.utility?.export ?? 0;
  const gensetElectrical = data?.componentsAttributes?.genset?.electrical ?? 0;
  const loadElectrical = data?.componentsAttributes?.load?.electrical ?? 0;
  const autoProdPercent = toNumber(scenario?.AutoProd);
  const hideSellingArtifacts =
    Number(batteryLoss) === 0 &&
    Number(pvElectrical) === 0 &&
    Number(gensetElectrical) === 0;

  const excessEnergyConnectedData = data?.tableData?.excessEnergyConnected ?? 0;
  const excessEnergyOffgridData = data?.tableData?.excessEnergyOffgrid ?? 0;
  // Add more variables here as needed for other attributes

  const selfProductionData = pvElectrical + gensetElectrical - utilityExport - batteryLoss - excessEnergyConnectedData - excessEnergyOffgridData;
  const totalConsumption = toNumber(loadElectrical);
  const selfProductionPercent = Math.abs((selfProductionData / totalConsumption) * 100).toFixed(2);
  const autoProductionData = (totalConsumption * autoProdPercent) / 100;

  // Battery visibility strictly when its numeric value > 0
  const showBattery = batteryLoss > 0;

  const flowData = {
    utility: {
      buying: `${formatEnUsValue(
        formatValueToPercentage(parseFloat(utilityImport))
      )} MWh`,
      selling: `${formatEnUsValue(
        formatValueToPercentage(parseFloat(utilityExport))
      )} MWh`,
    },
    load: `${formatEnUsValue(
      formatValueToPercentage(parseFloat(loadElectrical))
    )} MWh`,
    battery: `${formatEnUsValue(
      formatValueToPercentage(parseFloat(batteryLoss))
    )} MWh`,
    pv: `${formatEnUsValue(
      formatValueToPercentage(parseFloat(pvElectrical))
    )} MWh`,
    genset: `${formatEnUsValue(
      formatValueToPercentage(parseFloat(gensetElectrical))
    )} MWh`,
    auto_production: `${formatEnUsValue(
      formatValueToPercentage(parseFloat(selfProductionData))
    )} MWh`,
    totalConsumption: `${formatEnUsValue(parseFloat(totalConsumption))} MWh`,
    totalConsumptionPercentage: `${formatEnUsValue(
      parseFloat(totalConsumption ? (utilityImport / totalConsumption) * 100 : 0)
    )}%`,
    selfProductionPercentage: `${formatEnUsValue(
      parseFloat(selfProductionPercent)
    )}%`,
  };
  const { colorScheme } = useMantineColorScheme();
  const isDarkMode = colorScheme === "dark";

  // Colors for light/dark mode
  const COLORS = {
    bg: colorScheme === "dark" ? "#18191a" : "#fff",
    leftFlow: "#42B4E6",
    leftFlowGrey: colorScheme === "dark" ? "#23272b" : "#fff",
    rightFlow: "#3dcd58",
    siteIcon: "#3dcd58",
    text: colorScheme === "dark" ? "#fff" : "#222",
  };

  // Calculate battery, genset values as numbers (default to 0 if not present)
  // PV value logic is now handled in PVBar or upstream, only pass props here.
  let batteryValue = Number((flowData.battery || "0").toString().replace(/[^\d.]/g, "")) || 0;

  let gensetValue =
    Number((flowData.genset || "0").toString().replace(/[^\d.]/g, "")) || 0;
  const totalValue = batteryValue + gensetValue || 1; // avoid division by zero


  // --- Ensure all bars are visible with a minimum width, and scale larger values proportionally ---
  const MIN_BAR_WIDTH = 40; // px, minimum width for visibility
  const MAX_BAR_WIDTH = BAR_WIDTH - 2 * MIN_BAR_WIDTH; // px, max width for the largest value

  // Find the max value among battery, genset
const SEPARATOR_WIDTH = 15;

  // Reintroduce PV into proportional scaling so widths truly reflect relative magnitudes.
  // Parse PV numeric raw once (without formatting suffix) for width proportioning.
  const pvNumeric = Number((flowData.pv || "0").toString().replace(/[^\d.]/g, "")) || 0;
  const gensetNumeric = Number((flowData.genset || "0").toString().replace(/[^\d.]/g, "")) || 0;
  const showGenset = gensetNumeric > 0;

  // Active component flags (battery requires showBattery + value>0)
  const activeBattery = showBattery && batteryValue > 0;
  const activePV = pvNumeric > 0;
  const activeGenset = showGenset && gensetNumeric > 0;
  const activeCount = [activeBattery, activePV, activeGenset].filter(Boolean).length;

  let TOP_MAIN_WIDTH = 0;   // battery
  let TOP_SPLIT_WIDTH = 0;  // pv
  let TOP_WIND_WIDTH = 0;   // genset

  if (activeCount === 0) {
    // all widths remain zero
  } else if (activeCount === 1) {
    // single active takes full width
    if (activeBattery) TOP_MAIN_WIDTH = BAR_WIDTH;
    else if (activePV) TOP_SPLIT_WIDTH = BAR_WIDTH;
    else if (activeGenset) TOP_WIND_WIDTH = BAR_WIDTH;
  } else {
    // multiple: allocate minimum then scale remainder proportionally
    const minTotal = activeCount * MIN_BAR_WIDTH;
    let remaining = BAR_WIDTH - minTotal;
    if (remaining < 0) remaining = 0;
    const batteryScale = activeBattery ? batteryValue : 0;
    const pvScale = activePV ? pvNumeric : 0;
    const gensetScale = activeGenset ? gensetNumeric : 0;
    const scaleSum = batteryScale + pvScale + gensetScale || 1;
    if (activeBattery) TOP_MAIN_WIDTH = MIN_BAR_WIDTH;
    if (activePV) TOP_SPLIT_WIDTH = MIN_BAR_WIDTH;
    if (activeGenset) TOP_WIND_WIDTH = MIN_BAR_WIDTH;
    if (remaining > 0) {
      if (activeBattery) TOP_MAIN_WIDTH += remaining * (batteryScale / scaleSum);
      if (activePV) TOP_SPLIT_WIDTH += remaining * (pvScale / scaleSum);
      if (activeGenset) TOP_WIND_WIDTH += remaining * (gensetScale / scaleSum);
    }
  }



  const EXTRA_BAR_WIDTH = BAR_WIDTH * 0.4;
  const EXTRA_INIT_X = INIT_X + 59;
  const EXTRA_INIT_Y = 400;
  const EXTRA_INIT_TRANS_X = INIT_TRANS.x - EXTRA_BAR_WIDTH - 60;
  const EXTRA_INIT_TRANS_Y = INIT_TRANS.y;

  // Left flow state (drag bottom bar)
  const [bar, setBar] = useState({ x: INIT_X, y: INIT_Y });
  const [dragging, setDragging] = useState(false);

  // Right flow state (drag top bar, bottom bar is fixed at INIT_RY and INIT_RX_BOTTOM)
  const [barMirror, setBarMirror] = useState({ x: INIT_RX, y: INIT_RY_TOP });
  const [draggingMirror, setDraggingMirror] = useState(false);
  const dragOffsetMirror = useRef({ x: 0 });

  // Extra flow state (drag bottom bar)
  const [extraBar, setExtraBar] = useState({
    x: EXTRA_INIT_X,
    y: EXTRA_INIT_Y,
  });

  // Extra above bar state (drag)

  const attachedSplitBottomX = 50;

  const attachBottomRectX = attachedSplitBottomX + 20 - 130;


  const [attachBottomBar, setAttachBottomBar] = useState({
    x: attachBottomRectX,
  });

  const svgRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  // --- Highlight & Tooltip State ---
  const [hovered, setHovered] = useState(null); // e.g. 'left-flow'
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    text: "",
  });

  // --- Mouse handlers for left flow highlight & tooltip ---
  const handleLeftFlowMouseEnter = (e) => {
    setHovered("left-flow");
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      text: `${t("result.energyFlow.tooltip.buying")}: ${flowData?.utility?.buying}`,
    });
  };
  const handleToolTipPointerEvent = (e) => {
    setTooltip((t) =>
      t.visible ? { ...t, x: e.clientX, y: e.clientY } : t
    );
  };
  const handleLeftFlowMouseMove = (e) => {
    handleToolTipPointerEvent(e);
  };
  const handleLeftFlowMouseLeave = () => {
    setHovered(null);
    setTooltip({ visible: false, x: 0, y: 0, text: "" });
  };

  //   mouse handler for load flow
  const handleLoadFlowMouseEnter = (e) => {
    setHovered("load-flow");
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      text: `${t("result.energyFlow.tooltip.load")}: ${flowData?.load}`,
    });
  };
  const handleLoadFlowMouseMove = (e) => {
    handleToolTipPointerEvent(e);
  };
  const handleLoadFlowMouseLeave = () => {
    setHovered(null);
    setTooltip({ visible: false, x: 0, y: 0, text: "" });
  };

  // --- Mouse handlers for right main flow highlight & tooltip ---
  const handleRightMainFlowMouseEnter = (e) => {
    setHovered("right-main-flow");
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      text: `${t("result.energyFlow.tooltip.battery")}: ${flowData?.battery}`,
    });
  };
  const handleRightMainFlowMouseMove = (e) => {
    handleToolTipPointerEvent(e);
  };
  const handleRightMainFlowMouseLeave = () => {
    setHovered(null);
    setTooltip({ visible: false, x: 0, y: 0, text: "" });
  };

  // --- Mouse handlers for right split flow highlight & tooltip ---
  const handleRightSplitFlowMouseEnter = (e) => {
    setHovered("right-split-flow");
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      text: `${t("result.energyFlow.tooltip.pv")}: ${flowData?.pv}`,
    });
  };
  const handleRightSplitFlowMouseMove = (e) => {
    handleToolTipPointerEvent(e);
  };
  const handleRightSplitFlowMouseLeave = () => {
    setHovered(null);
    setTooltip({ visible: false, x: 0, y: 0, text: "" });
  };

  // Mouse event handlers for left flow (drag bottom bar)
  const onMouseDown = (e) => {
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left - INIT_TRANS.x;
    const mouseY = e.clientY - svgRect.top - INIT_TRANS.y;
    dragOffset.current = {
      x: mouseX - bar.x,
      y: mouseY - bar.y,
    };
    setDragging(true);
    e.preventDefault();
  };

  // Mouse event handlers for right flow (drag top bar, bottom bar is fixed)
  const onMouseDownMirror = (e) => {
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left - INIT_TRANS.x - MIRROR_X_OFFSET;
    const mouseY = e.clientY - svgRect.top - INIT_TRANS.y;
    dragOffsetMirror.current = {
      x: mouseX - barMirror.x,
      y: mouseY - barMirror.y,
    };
    setDraggingMirror(true);
    e.preventDefault();
  };

  // Mouse event handlers for left top bar (drag)
  const [draggingLeftTop, setDraggingLeftTop] = useState(false);
  const dragOffsetLeftTop = useRef({ x: 0, y: 0 });
  const [barTop, setBarTop] = useState({ x: 0, y: 0 }); // NEW: left top bar position

  const onMouseDownLeftTop = (e) => {
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - svgRect.left - INIT_TRANS.x;
    const mouseY = e.clientY - svgRect.top - INIT_TRANS.y;
    dragOffsetLeftTop.current = {
      x: mouseX - barTop.x,
      y: mouseY - barTop.y,
    };
    setDraggingLeftTop(true);
    e.preventDefault();
  };

  // Sankey-like curve for left flow (top bar at barTop, bottom bar at bar)
  const filledCurve = getLeftFlowPath(barTop.x, barTop.y, bar.x, bar.y, BAR_WIDTH, BAR_HEIGHT);
 
  // Sankey-like curve for right flow (top bar draggable, bottom bar fixed at INIT_RX_BOTTOM, INIT_RY)
  // Only 80% (main) is blue, 20% (split) is orange
  const rightBarOffset = flowData?.utility?.selling ? SEPARATOR_WIDTH : 0;
  const filledCurveMirrorMain = getRightMainFlowPath(
    barMirror.x + rightBarOffset, // top bar x (shifted)
    barMirror.y,
    BAR_HEIGHT,
    TOP_MAIN_WIDTH,
    INIT_RX_BOTTOM, // bottom bar x (NOT shifted)
    INIT_RY
  );

  // Set the bottom bar's x to 30
  const attachedSplitBottomY = 0;

  // --- Calculate width for right-top-split-attach (selling bar) ---
  const sellingValue =
    Number(
      (flowData.utility?.selling || "0").toString().replace(/[^\d.]/g, "")
    ) || 0;
  const sellingPercent = totalValue > 0 ? sellingValue / totalValue : 0;
  const attachedSplitWidth = totalValue > 0 ? BAR_WIDTH * sellingPercent : 0;

  // Small bar width should be the totalValue's % of selling value
  const smallBarWidth =
    attachedSplitWidth > 120
      ? 120
      : attachedSplitWidth < 50
      ? 100
      : attachedSplitWidth;
  const smallBarHeight = BAR_HEIGHT;
  const smallBarX = attachBottomBar.x + attachedSplitWidth;
  const smallBarY = attachedSplitBottomY;

  // Separator bar width for right-top-main-bar-left-bar
 

  let splitBarGapY = 0;
  let splitBarGapX = 0;

  let splitBarGapWidth = 0;

  // Small bar (20px wide) directly attached to the right of right-top-split-attach-bottom (no gap)
  const barXOffset = 10;

  // --- Reset handler ---
  const handleReset = () => {
    setBar({ x: INIT_X, y: INIT_Y });
    setBarMirror({ x: INIT_RX, y: INIT_RY_TOP });
    setExtraBar({ x: EXTRA_INIT_X, y: EXTRA_INIT_Y });
  };

  // --- Download handler ---
  const handleDownload = () => {
    const svg = svgRef.current;
    const svgContainer = svgContainerRef?.current;
    if (!svg || !svgContainer) return;

    // Ensure correct theme class for export
    const seApp = document.querySelector('se-app');
    const isDarkMode = seApp?.classList.contains('se-theme-dark');
    if (isDarkMode) {
      svgContainer.classList.add('se-theme-dark');
    } else {
      svgContainer.classList.remove('se-theme-dark');
    }

    // --- original SVG export logic ---
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);

    // Add name space if missing.
    let svgData = source;
    if (!source.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
      svgData = source.replace(
        /^<svg/,
        '<svg xmlns="http://www.w3.org/2000/svg"'
      );
    }
    // Add xml declaration
    svgData = '<?xml version="1.0" standalone="no"?>\r\n' + svgData;

    // Convert SVG to image
    const url =
      "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgData);
    const img = new window.Image();
    img.onload = function () {
      const canvas = document.createElement("canvas");
      canvas.width = svg.width.baseVal.value;
      canvas.height = svg.height.baseVal.value;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = isDarkMode ? "#111111" : "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png;
      a.download = "energyflow.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };
    img.src = url;
  };

  // Check if utility.buying data is available
  const hasUtilityBuying = flowData?.utility?.buying;

  const siteIconX = hasUtilityBuying
    ? EXTRA_INIT_TRANS_X + extraBar.x - 160
    : EXTRA_INIT_TRANS_X + extraBar.x - 160 + 220;
  const siteIconY = EXTRA_INIT_TRANS_Y + extraBar.y + EXTRA_BAR_HEIGHT + 8;

  // Animation state for SVG group
  // const [animate, setAnimate] = useState(false);
  // const [animationPaused, setAnimationPaused] = useState(false);
  const svgContainerRef = useRef(null);

  // Helper to force re-render SVG gradients with/without <animate>
  
  // --- Dynamically calculate gap and positions for right-top-split-gap-bar and related elements ---

  // Minimum gap is 24, but if battery or pv bar is very small, increase gap to avoid overlap
  const MIN_SPLIT_GAP = 24;
  const dynamicSplitGap = activeCount > 1 ? Math.max(
    MIN_SPLIT_GAP,
    0.2 * Math.max(TOP_MAIN_WIDTH, TOP_SPLIT_WIDTH, TOP_WIND_WIDTH, 1)
  ) : 0;

  // Position logic: components appear in fixed order Battery -> PV -> Genset among active ones
  let pvSplitXDynamic = barMirror.x + rightBarOffset; // default
  if (activeBattery) {
    pvSplitXDynamic = barMirror.x + rightBarOffset + TOP_MAIN_WIDTH + (activePV ? dynamicSplitGap : 0);
  } else {
    // battery not active: pv starts at barMirror.x if active
    pvSplitXDynamic = barMirror.x + rightBarOffset;
  }
  const pvSplitWidthDynamic = TOP_SPLIT_WIDTH;
  let windSplitXDynamic = pvSplitXDynamic;
  if (activeBattery && activePV) {
    windSplitXDynamic = pvSplitXDynamic + pvSplitWidthDynamic + (activeGenset ? dynamicSplitGap : 0);
  } else if (!activeBattery && activePV) {
    windSplitXDynamic = pvSplitXDynamic + pvSplitWidthDynamic + (activeGenset ? dynamicSplitGap : 0);
  } else if (activeBattery && !activePV) {
    // PV inactive, genset after battery
    windSplitXDynamic = barMirror.x + rightBarOffset + TOP_MAIN_WIDTH + (activeGenset ? dynamicSplitGap : 0);
  } else {
    windSplitXDynamic = barMirror.x + rightBarOffset; // only genset
  }
  const windSplitWidthDynamic = activeGenset ? TOP_WIND_WIDTH : 0;

  // Always define splitBarGapWidth, splitBarGapX, splitBarGapY with safe defaults
  // let splitBarGapY = 0;
  if (TOP_SPLIT_WIDTH > 0) {
    splitBarGapWidth = dynamicSplitGap - 4 > 0 ? dynamicSplitGap - 4 : 0;
    splitBarGapX = pvSplitXDynamic - splitBarGapWidth;
    splitBarGapY = barMirror.y;
  }

  // Define these curves here so they are always available before use
  const filledCurveMirrorSplitDynamic = getRightSplitFlowPath(
    pvSplitXDynamic,
    barMirror.y,
    BAR_HEIGHT,
    pvSplitWidthDynamic,
    windSplitWidthDynamic,
    INIT_RX_BOTTOM, // bottom bar x (NOT shifted)
    INIT_RY,
    BAR_WIDTH
  );

  const filledCurveMirrorWindDynamic = getRightWindFlowPath(
    windSplitXDynamic,
    barMirror.y,
    BAR_HEIGHT,
    windSplitWidthDynamic,
    INIT_RX_BOTTOM, // bottom bar x (NOT shifted)
    BAR_WIDTH,
    INIT_RY
  );

  return (
    <div
      ref={svgContainerRef}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        backgroundColor: isDarkMode ? "#111111" : "#fff",
        // position: "relative",
        alignItems: "center",
        height: "100%", // ensure height is 100% for fullscreen centering
      }}
      className={
        typeof document !== "undefined" &&
        document.fullscreenElement === svgContainerRef.current
          ? "energyflow-fullscreen"
          : ""
      }
    >
      {/* Top right icons */}
   
      {/* Tooltip */}
      {tooltip.visible && (
        <div
          className={styles.energyflowTooltip}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {hovered === "left-flow" && (
              <span style={{ width: 16, height: 16, background: COLORS.leftFlow, borderRadius: 4, display: "inline-block", border: "1px solid #eee" }} />
            )}
            {hovered === "load-flow" && (
              <span style={{ width: 16, height: 16, background: "#E47F00", borderRadius: 4, display: "inline-block", border: "1px solid #eee" }} />
            )}
            {hovered === "right-main-flow" && (
              <span style={{ width: 16, height: 16, background: COLORS.rightFlow, borderRadius: 4, display: "inline-block", border: "1px solid #eee" }} />
            )}
            {hovered === "right-split-flow" && (
              <span style={{ width: 16, height: 16, background: "#009530", borderRadius: 4, display: "inline-block", border: "1px solid #eee" }} />
            )}
            {hovered === "right-genset-flow" && (
              <span style={{ width: 16, height: 16, background: "#24B43F", borderRadius: 4, display: "inline-block", border: "1px solid #eee" }} />
            )}
            {hovered === "selling-flow" && (
              <span style={{ width: 16, height: 16, background: "#0F81B3", borderRadius: 4, display: "inline-block", border: "1px solid #eee" }} />
            )}
            <span>{tooltip.text}</span>
          </div>
        </div>
      )}
      <svg
        ref={svgRef}
        id="energyflow-svg"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        baseProfile="full"
        width={SVG_WIDTH}
        height={SVG_HEIGHT}
        viewBox={`-100 -100 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        // key={animationKey} // force re-render on animation toggle
        // style={{
        //   display: "block",
        //   margin: "0 auto",
        //   ...(typeof document !== "undefined" &&
        //   document.fullscreenElement === svgContainerRef.current
        //     ? {
        //         // position: "absolute",
        //         // left: "50%",
        //         // top: "50%",
        //         // transform: "translate(-50%, -50%)",
        //         // maxWidth: "90vw",
        //         // maxHeight: "90vh",
        //       }
        //     : {}),
        // }}
      >
        <rect
          id="energyflow-bg"
          width={SVG_WIDTH}
          height={SVG_HEIGHT}
          x="90"
          y="0"
          fill="none"
        />
        <g
          id="energyflow-main-group"
        >
                   {/* Left (original) flow */}
          {hasUtilityBuying && (
            <g id="utility-buying" transform="translate(-40,0)">
              <path
                id="energyflow-left-curve"
                d={filledCurve}
                transform={`translate(${INIT_TRANS.x} ${INIT_TRANS.y})`}
                fill="#219bfd"
                fillOpacity={hovered === "left-flow" ? 0.7 : 0.8}
                style={{ cursor: "pointer" }}
                onMouseEnter={handleLeftFlowMouseEnter}
                onMouseMove={handleLeftFlowMouseMove}
                onMouseLeave={handleLeftFlowMouseLeave}
              />
              {/* Grey overlay bar for left flow (above top bar, below bottom bar) */}
              <rect
                id="energyflow-left-top-grey"
                x={barTop.x}
                y={barTop.y - BAR_HEIGHT - 30}
                width={BAR_WIDTH}
                height={BAR_HEIGHT + 30}
                transform={`translate(${INIT_TRANS.x} ${INIT_TRANS.y})`}
                fill={COLORS.leftFlowGrey}
                fillOpacity={hovered === "left-flow" ? 1 : 0.7}
                style={{ cursor: "pointer" }}
                onMouseEnter={handleLeftFlowMouseEnter}
                onMouseMove={handleLeftFlowMouseMove}
                onMouseLeave={handleLeftFlowMouseLeave}
              />
              {/* Example data inside grey bar (left), aligned left and font sizes reduced */}
              <text
                id="energyflow-left-top-grey-text"
                x={INIT_TRANS.x + barTop.x + 12}
                y={INIT_TRANS.y + barTop.y - BAR_HEIGHT - 30 + 18}
                textAnchor="start"
                fill={COLORS.leftFlow}
              >
                <tspan
                  x={INIT_TRANS.x + barTop.x + 12}
                  dy="-6"
                  fontSize="11"
                  fontWeight="400"
                >
                  Buying
                </tspan>
                <tspan
                  x={INIT_TRANS.x + barTop.x + 12}
                  dy="22"
                  fontSize="20"
                  fontWeight="700"
                >
                  {flowData?.utility?.buying}
                </tspan>
                <tspan
                  x={INIT_TRANS.x + barTop.x + 12}
                  dy="14"
                  fontSize="11"
                  fontWeight="400"
                >
                  from utility
                </tspan>
                {/* Add a small gap below the last tspan to prevent overlap */}
                <tspan x={INIT_TRANS.x + barTop.x + 12} dy="8" fontSize="1">
                  &#8203;
                </tspan>
              </text>
              {/* Grey overlay bar for left flow (below bottom bar) */}
              <rect
                id="energyflow-left-bottom-grey"
                x={bar.x}
                y={bar.y + 10 + 8}
                width={BAR_WIDTH}
                height={BAR_HEIGHT + 30}
                transform={`translate(${INIT_TRANS.x} ${INIT_TRANS.y})`}
                fill={COLORS.leftFlowGrey}
                fillOpacity={hovered === "left-flow" ? 1 : 0.7}
                style={{ cursor: "pointer" }}
                onMouseEnter={handleLeftFlowMouseEnter}
                onMouseMove={handleLeftFlowMouseMove}
                onMouseLeave={handleLeftFlowMouseLeave}
              />
              {/* Example data inside bottom grey bar (left), aligned left and font sizes reduced */}
              {(() => {
                // Dynamic font size calculation for left-bottom-grey bar
                const valueText = `${flowData?.utility?.buying} | ${flowData?.totalConsumptionPercentage}`;
                const maxWidth = BAR_WIDTH - 24;
                // Estimate width: assume average char width = 0.6 * fontSize (px)
                let fontSize = 20;
                const minFontSize = 11;
                const avgCharWidth = 0.6;
                if (valueText && valueText.length * fontSize * avgCharWidth > maxWidth) {
                  fontSize = Math.max(
                    minFontSize,
                    Math.floor(maxWidth / (valueText.length * avgCharWidth))
                  );
                }
                return (
                  <text
                    id="energyflow-left-bottom-grey-text"
                    x={INIT_TRANS.x + bar.x + 12}
                    y={INIT_TRANS.y + bar.y + BAR_HEIGHT + 14}
                    textAnchor="start"
                    fill={COLORS.leftFlow}
                  >
                    <tspan
                      x={INIT_TRANS.x + bar.x + 12}
                      dy="10"
                      fontSize={fontSize}
                      fontWeight="700"
                    >
                      {valueText}
                    </tspan>
                    <tspan
                      x={INIT_TRANS.x + bar.x + 12}
                      dy="14"
                      fontSize="11"
                      fontWeight="400"
                    >
                      from utility
                    </tspan>
                  </text>
                );
              })()}
              <rect
                id="energyflow-left-top-bar"
                x={barTop.x}
                y={barTop.y}
                width={BAR_WIDTH}
                height={BAR_HEIGHT}
                transform={`translate(${INIT_TRANS.x} ${INIT_TRANS.y})`}
                fill={COLORS.leftFlow}
                onMouseDown={onMouseDownLeftTop}
              />
              <g
                id="energyflow-left-top-bar-icon"
                transform={`translate(${INIT_TRANS.x + barTop.x - 68} ${INIT_TRANS.y + barTop.y - 14}) scale(${hovered === "left-flow" ? 1.2 : 1})`}
                style={{ transition: "transform 0.35s cubic-bezier(.4,0,.2,1)" }}
              >
                <circle
                  cx={26}
                  cy={26}
                  r={24}
                  fill="#219bfd"
                  stroke="#219bfd"
                  strokeWidth={2}
                />
                {/* Center the GridIcon in the circle */}
                <g transform="translate(10,10)">
                  <GridIcon width={32} height={32} fill="white" />
                </g>
              </g>
              <rect
                id="energyflow-left-bottom-bar"
                x={bar.x}
                y={bar.y}
                width={BAR_WIDTH}
                height={BAR_HEIGHT}
                transform={`translate(${INIT_TRANS.x} ${INIT_TRANS.y})`}
                fill={COLORS.leftFlow}
                onMouseDown={onMouseDown}
              />
            </g>
          )}
          <g id="right-utility-flow" transform="translate(-40,0)">
            {/* Info bar group */}
            <g id="info-bar-group">
              <rect
                id="energyflow-right-bottom-grey"
                x={INIT_RX_BOTTOM}
                y={INIT_RY + 10 + 8}
                width={BAR_WIDTH}
                height={BAR_HEIGHT + 30}
                transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                  INIT_TRANS.y
                })`}
                fill={COLORS.leftFlowGrey}
                fillOpacity={hovered === "right-main-flow" ? 1 : 0.7}
                style={{ cursor: "pointer" }}
                onMouseEnter={handleRightMainFlowMouseEnter}
                onMouseMove={handleRightMainFlowMouseMove}
                onMouseLeave={handleRightMainFlowMouseLeave}
              />
              {/* existing auto production text */}
              {(() => {
                // Dynamic font size calculation for right-bottom-grey bar
                const valueText = `${flowData?.auto_production} | ${flowData?.selfProductionPercentage}`;
                const maxWidth = BAR_WIDTH - 24;
                // Estimate width: assume average char width = 0.6 * fontSize (px)
                let fontSize = 20;
                const minFontSize = 11;
                const avgCharWidth = 0.6;
                if (valueText && valueText.length * fontSize * avgCharWidth > maxWidth) {
                  fontSize = Math.max(
                    minFontSize,
                    Math.floor(maxWidth / (valueText.length * avgCharWidth))
                  );
                }
                return (
                  <text
                    id="energyflow-right-bottom-grey-text"
                    x={INIT_TRANS.x + MIRROR_X_OFFSET + INIT_RX_BOTTOM + 12}
                    y={INIT_TRANS.y + INIT_RY + BAR_HEIGHT + 18}
                    textAnchor="start"
                    fill={COLORS.rightFlow}
                  >
                    <tspan
                      x={INIT_TRANS.x + MIRROR_X_OFFSET + INIT_RX_BOTTOM + 12}
                      dy="5"
                      fontSize={fontSize}
                      fontWeight="700"
                    >
                      {valueText}
                    </tspan>
                    <tspan
                      x={INIT_TRANS.x + MIRROR_X_OFFSET + INIT_RX_BOTTOM + 12}
                      dy="14"
                      fontSize="11"
                      fontWeight="400"
                    >
                      from Self Production
                    </tspan>
                  </text>
                );
              })()}
            </g>
            {/* Restored bottom bar aggregating auto-production flows */}
            <rect
              id="right-bottom-bar"
              x={INIT_RX_BOTTOM}
              y={INIT_RY}
              width={BAR_WIDTH}
              height={BAR_HEIGHT}
              transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${INIT_TRANS.y})`}
              fill={COLORS.rightFlow}
              fillOpacity={hovered === "right-split-flow" ? 1 : 0.9}
              style={{ cursor: "pointer" }}
              onMouseEnter={handleRightSplitFlowMouseEnter}
              onMouseMove={handleRightSplitFlowMouseMove}
              onMouseLeave={handleRightSplitFlowMouseLeave}
            />
            {/* Selling left bar */}
            {flowData?.utility?.selling && !hideSellingArtifacts && (
              <g id="selling-left-bar-group">
                <rect
                  id="right-top-main-bar-left-bar"
                  x={barMirror.x - smallBarWidth}
                  y={barMirror.y}
                  width={smallBarWidth}
                  height={BAR_HEIGHT}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                    INIT_TRANS.y
                  })`}
                  fill={"#0F81B3"}
                  fillOpacity={hovered === "right-main-flow" ? 1 : 0.9}
                />
                {/* Small separator bar to the right of the left bar */}
                <rect
                  id="right-top-main-bar-left-bar-separator"
                  x={barMirror.x}
                  y={barMirror.y }
                  width={SEPARATOR_WIDTH}
                  height={BAR_HEIGHT}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${INIT_TRANS.y})`}
                  fill="#3dcd58"
                  // stroke="#0F81B3"
                  // strokeWidth={1}
                  // rx={2}
                  // style={{ pointerEvents: 'none' }}
                />
              </g>
            )}
            {/* Battery top bar + icon */}
            {activeBattery && (
            <g id="battery-top-group">
              <rect
                id="right-top-main-bar"
                x={barMirror.x + (flowData?.utility?.selling ? SEPARATOR_WIDTH : 0)}
                y={barMirror.y}
                width={TOP_MAIN_WIDTH}
                height={BAR_HEIGHT}
                transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                  INIT_TRANS.y
                })`}
                fill={COLORS.rightFlow}
                fillOpacity={hovered === "right-main-flow" ? 1 : 0.9}
                onMouseDown={onMouseDownMirror}
                onMouseEnter={handleRightMainFlowMouseEnter}
                onMouseMove={handleRightMainFlowMouseMove}
                onMouseLeave={handleRightMainFlowMouseLeave}
              />
              {activeBattery && (
                <g
                  id="right-main-curve-battery-icon"
                  transform={`translate(${
                    INIT_TRANS.x +
                    MIRROR_X_OFFSET +
                    barMirror.x +
                    TOP_MAIN_WIDTH / 2 -
                    15
                  } ${INIT_TRANS.y + barMirror.y - 80}) scale(${hovered === 'right-main-flow' ? 1.08 : 1})`}
                  style={{
                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transformOrigin: '26px 26px',
                  }}
                >
                  <circle
                    cx={26}
                    cy={26}
                    r={26}
                    fill={COLORS.rightFlow}
                    stroke={COLORS.rightFlow}
                    strokeWidth={2}
                  />
                  <g transform="translate(10,10)">
                    <BatteryIcon width={32} height={32} fill="white" />
                  </g>
                  <text
                    x={26}
                    y={68}
                    textAnchor="middle"
                    fill={COLORS.rightFlow}
                    fontSize="12"
                    fontWeight="700"
                  >
                    &nbsp;&nbsp;{flowData.battery}
                  </text>
                </g>
              )}
            </g>
            )}
            {/* Battery to PV gap */}
            {activeBattery && TOP_SPLIT_WIDTH > 0 && activePV && (
              <g id="battery-to-pv-gap-group">
                <rect
                  id="right-top-main-to-split-gap-bar"
                  x={barMirror.x + TOP_MAIN_WIDTH + (flowData?.utility?.selling ? SEPARATOR_WIDTH : 0)}
                  y={barMirror.y}
                  width={pvSplitXDynamic - (barMirror.x + TOP_MAIN_WIDTH + (flowData?.utility?.selling ? SEPARATOR_WIDTH : 0))}
                  height={BAR_HEIGHT}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                    INIT_TRANS.y
                  })`}
                  fill={COLORS.rightFlow}
                  fillOpacity={hovered === "right-main-flow" ? 1 : 0.9}
                />
              </g>
            )}
            {/* Battery to Genset connector when PV absent */}
            {activeBattery && activeGenset && !activePV && (
              <g id="battery-to-genset-gap-group">
                <rect
                  id="right-top-main-to-genset-gap-bar"
                  x={barMirror.x + TOP_MAIN_WIDTH + SEPARATOR_WIDTH}
                  y={barMirror.y}
                  width={windSplitXDynamic - (barMirror.x + TOP_MAIN_WIDTH + (flowData?.utility?.selling ? SEPARATOR_WIDTH : 0))}
                  height={BAR_HEIGHT}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${INIT_TRANS.y})`}
                  fill={COLORS.rightFlow}
                  fillOpacity={hovered === 'right-main-flow' ? 1 : 0.9}
                />
              </g>
            )}
            {/* PV bar rendered via PVBar component earlier; legacy inline block removed */}
            {/* PV to Genset gap fill (only when genset present & selling) */}
            {activeGenset && activePV && flowData?.utility?.selling && (
              <g id="pv-to-genset-gap-group">
                <rect
                  id="right-top-split-bar-gap-fill"
                  x={pvSplitXDynamic + pvSplitWidthDynamic}
                  y={barMirror.y}
                  width={windSplitXDynamic - (pvSplitXDynamic + pvSplitWidthDynamic)}
                  height={BAR_HEIGHT}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                    INIT_TRANS.y
                  })`}
                  fill={"#009530"}
                  fillOpacity={hovered === "right-split-flow" ? 1 : 0.9}
                />
              </g>
            )}
            {/* Genset top bar + icon (shown only if genset > 0) */}
            {activeGenset && (
              <g transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${INIT_TRANS.y})`}>
                <GensetBar
                  x={windSplitXDynamic}
                  y={barMirror.y}
                  width={windSplitWidthDynamic}
                  height={BAR_HEIGHT}
                  iconX={windSplitXDynamic + windSplitWidthDynamic / 2 - 26}
                  iconY={barMirror.y - 80}
                  value={flowData.genset}
                  fill="#24B43F"
                  fillOpacity={hovered === "right-genset-flow" ? 1 : 0.9}
                  iconGroupScale={hovered === "right-genset-flow" ? 1.08 : 1}
                  iconGroupTransition={
                    'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }
                  onMouseEnter={(e) => {
                    setHovered("right-genset-flow");
                    setTooltip({
                      visible: true,
                      x: e.clientX,
                      y: e.clientY - 32,
                      text: `${t("result.energyFlow.tooltip.genset")}: ${flowData?.genset}`,
                    });
                  }}
                  onMouseMove={(e) =>
                    setTooltip((t) =>
                      t.visible ? { ...t, x: e.clientX, y: e.clientY - 32 } : t
                    )
                  }
                  onMouseLeave={() => {
                    setHovered(null);
                    setTooltip({ visible: false, x: 0, y: 0, text: "" });
                  }}
                />
              </g>
            )}
            {/* Curves grouped separately to avoid reordering */}
            {activeBattery && (
              <g id="battery-curve-group">
                <path
                  id="right-main-curve"
                  d={filledCurveMirrorMain}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                    INIT_TRANS.y
                  })`}
                  fill="#3DCD58"
                  fillOpacity={hovered === "right-main-flow" ? 0.7 : 0.8}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={handleRightMainFlowMouseEnter}
                  onMouseMove={handleRightMainFlowMouseMove}
                  onMouseLeave={handleRightMainFlowMouseLeave}
                />
              </g>
            )}
            {activePV && (
            <g id="pv-curve-group">
              <path
                id="right-split-curve"
                d={filledCurveMirrorSplitDynamic}
                transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                  INIT_TRANS.y
                })`}
                fill="#009530"
                fillOpacity={hovered === "right-split-flow" ? 0.7 : 0.8}
                style={{ cursor: "pointer" }}
                onMouseEnter={handleRightSplitFlowMouseEnter}
                onMouseMove={handleRightSplitFlowMouseMove}
                onMouseLeave={handleRightSplitFlowMouseLeave}
              />
            </g>
            )}
            {activeGenset && (
            <g id="genset-curve-group">
              <path
                id="right-genset-curve"
                d={filledCurveMirrorWindDynamic}
                transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                  INIT_TRANS.y
                })`}
                fill="#24B43F"
                fillOpacity={hovered === "right-genset-flow" ? 0.7 : 0.8}
                style={{ cursor: "pointer" }}
                onMouseEnter={(e) => {
                  setHovered("right-genset-flow");
                  setTooltip({
                    visible: true,
                    x: e.clientX,
                    y: e.clientY - 32,
                    text: `${t("result.energyFlow.tooltip.genset")}: ${flowData?.genset}`,
                  });
                }}
                onMouseMove={(e) =>
                  setTooltip((t) =>
                    t.visible ? { ...t, x: e.clientX, y: e.clientY - 32 } : t
                  )
                }
                onMouseLeave={() => {
                  setHovered(null);
                  setTooltip({ visible: false, x: 0, y: 0, text: "" });
                }}
              />
            </g>
            )}
            {/* Selling curve + small bar */}
            {flowData?.utility?.selling && !hideSellingArtifacts && (
              <g id="selling-curve-small-bar-group">
                <path
                  id="right-top-main-bar-left-to-small-bar-bottom-curve"
                  d={`
                    M${barMirror.x - smallBarWidth} ${barMirror.y}
                    C${barMirror.x - smallBarWidth} ${barMirror.y - 40},
                     ${attachBottomBar.x} ${smallBarY + smallBarHeight + 40},
                     ${attachBottomBar.x} ${smallBarY + smallBarHeight}
                    L${attachBottomBar.x + smallBarWidth} ${
                    smallBarY + smallBarHeight
                  }
                    C${attachBottomBar.x + smallBarWidth} ${
                    smallBarY + smallBarHeight + 40
                  },
                     ${barMirror.x} ${barMirror.y - 40},
                     ${barMirror.x} ${barMirror.y}
                    Z
                  `}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                    INIT_TRANS.y
                  })`}
                  fill={"#0F81B3"}
                  fillOpacity={0.5}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => {
                    setHovered('selling-curve-gap');
                    setTooltip({
                      visible: true,
                      x: e.clientX,
                      y: e.clientY - 32,
                      text: `${t("result.energyFlow.tooltip.selling")}: ${flowData?.utility?.selling}`,
                    });
                  }}
                  onMouseMove={e =>
                    setTooltip(t =>
                      t.visible ? { ...t, x: e.clientX, y: e.clientY - 32 } : t
                    )
                  }
                  onMouseLeave={() => {
                    setHovered(null);
                    setTooltip({ visible: false, x: 0, y: 0, text: '' });
                  }}
                />
                <rect
                  id="right-top-split-attach-bottom-small-bar"
                  x={attachBottomBar.x}
                  y={smallBarY}
                  width={smallBarWidth}
                  height={smallBarHeight}
                  transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${
                    INIT_TRANS.y
                  })`}
                  fill={"#0F81B3"}
                  fillOpacity={1}
                />
              </g>
            )}
                  {flowData?.utility?.selling && !hideSellingArtifacts && (
              <g id="selling-info-group">
                <g id="utility-selling">
                  {/* Restored original grey text container (re-added id: right-top-split-attach-bottom-grey-text) */}
                  <text
                    id="right-top-split-attach-bottom-grey-text"
                    transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${INIT_TRANS.y})`}
                    x={attachBottomBar.x + 12}
                    y={smallBarY - (smallBarHeight + 12)}
                    textAnchor="start"
                    fill={"#0F81B3"}
                    // style={{ pointerEvents: 'none' }}
                  >
                    <tspan
                      x={attachBottomBar.x + 12}
                      dy="-6"
                      fontSize="11"
                      fontWeight="400"
                    >
                      Selling
                    </tspan>
                    <tspan
                      x={attachBottomBar.x + 12}
                      dy="22"
                      fontSize="20"
                      fontWeight="700"
                    >
                      {flowData?.utility?.selling}
                    </tspan>
                    <tspan
                      x={attachBottomBar.x + 12}
                      dy="14"
                      fontSize="11"
                      fontWeight="400"
                    >
                      to utility
                    </tspan>
                    <tspan x={attachBottomBar.x + 12} dy="8" fontSize="1">
                      &#8203;
                    </tspan>
                  </text>
                </g>
              </g>
            )}
            {/* PV top bar + icon (modularized) */}
            {activePV && (
              <g transform={`translate(${INIT_TRANS.x + MIRROR_X_OFFSET} ${INIT_TRANS.y})`}>
                <PVBar
                  x={pvSplitXDynamic}
                  y={barMirror.y}
                  width={pvSplitWidthDynamic}
                  height={BAR_HEIGHT}
                  iconX={pvSplitXDynamic + pvSplitWidthDynamic / 2 - 26}
                  iconY={barMirror.y - 80}
                  value={flowData.pv}
                  fill="#009530"
                  fillOpacity={hovered === "right-split-flow" ? 1 : 0.9}
                  iconGroupScale={hovered === "right-split-flow" ? 1.08 : 1}
                  iconGroupTransition={'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'}
                  onMouseEnter={handleRightSplitFlowMouseEnter}
                  onMouseMove={handleRightSplitFlowMouseMove}
                  onMouseLeave={handleRightSplitFlowMouseLeave}
                />
              </g>
            )}
          </g>
          <g id="load" transform="translate(153,470)">
            <path
              d={"M0,0 L480,0 L480,50 L0,50 Z"}
              fill="#E47F00"
              fillOpacity={hovered === "load-flow" ? 0.5 : 0.2}
              style={{ cursor: "pointer" }}
              onMouseEnter={handleLoadFlowMouseEnter}
              onMouseMove={handleLoadFlowMouseMove}
              onMouseLeave={handleLoadFlowMouseLeave}
            />

            <g transform="translate(130,6)">
              <LoadIcon
                width={40}
                height={40}
                fill={COLORS.rightFlow}
                strokeWidth={4}
              />
            </g>

            <text x={0} y={-70} textAnchor="start" fill={COLORS.rightFlow}>
              <tspan x={180} dy="95" fontSize="20" fontWeight="700">
                {flowData?.load}
              </tspan>
              <tspan x={180} dy="15" fontSize="11" fontWeight="400">
                Total Consumption
              </tspan>
            </text>
            <rect
              width={480}
              height={20}
              fill="#E47F00"
              transform={`translate(0,50)`}
              style={{ cursor: "pointer" }}
              onMouseEnter={handleLoadFlowMouseEnter}
              onMouseMove={handleLoadFlowMouseMove}
              onMouseLeave={handleLoadFlowMouseLeave}
            />
          </g>
        </g>
      </svg>
    </div>
  );
};

EnergyFlow.propTypes = {
  data: PropTypes.shape({
    componentsAttributes: PropTypes.shape({
      battery: PropTypes.shape({
        batteryLoss: PropTypes.number,
      }),
      genset: PropTypes.shape({
        electrical: PropTypes.number,
      }),
      utility: PropTypes.shape({
        import: PropTypes.number,
        export: PropTypes.number,
      }),
      pv: PropTypes.shape({
        electrical: PropTypes.number,
      }),
      load: PropTypes.shape({
        electrical: PropTypes.number,
      }),
    }),
  }),
  scenario: PropTypes.shape({
    AutoProd: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default EnergyFlow;
